import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { InrixService } from './inrix.service';
import { HttpClient } from '@angular/common/http';
import { first, delay } from 'rxjs/operators';
import { JsonPipe } from '@angular/common';

/**
 * Conté la informació necessària per mostrar una ruta calculada pel servei Inrix
 */
interface InrixData {
  /**
   * Punts per dibuixar la ruta
   */
  routePoints: google.maps.LatLng[],

  /**
   * Duració de la ruta
   */
  duration: string,

  /**
   * Distància total de la ruta
   */
  distance: string,

  /**
   * Consum de combustible de la ruta
   */
  fuel: string,

  /**
   * Resum de la ruta
   */
  summary: string,

  /**
   * Límits en que es pot veure tota la ruta 
   */
  bounds: google.maps.LatLngBounds
  
}

/**
 * Servei que permet fer càlcul de rutes i mostrar-les al mapa, connectant-se al servei Google Maps Directions i Inrix
 */
@Injectable({
  providedIn: 'root'
})
export class DirectionsService {

  /**
   * Instància del mapa
   */
  map: google.maps.Map;

  /**
   * Servei per buscar ubicacions al mapa
   */
  placesService: google.maps.places.PlacesService;

  /**
   * Instància de Google Maps Directions
   */
  directionsService = new google.maps.DirectionsService();

  /**
   * S'ocupa de renderitzar els resultats de DirectionsService
   */
  directionsRenderer = new google.maps.DirectionsRenderer({
    polylineOptions: {
      strokeColor: "#000CFF",
      strokeOpacity: 1,
      strokeWeight: 5,
      zIndex: 1
    },
    suppressMarkers: true,
    hideRouteList: true
  });

  /**
   * Altres Renderers que serveixen per dibuixar rutes que no estan seleccionades
   */
  directionsPreviewRenderer = new google.maps.DirectionsRenderer({
    polylineOptions: {
      strokeColor: "#000CFF",
      strokeOpacity: .4,
      strokeWeight: 5,
      zIndex: 1
    },
    preserveViewport: true,
    suppressMarkers: true
  });

  /**
   * Indica si la ruta no s'ha trobat
   */
  private _routeNotFound = new BehaviorSubject<boolean>(false);

  /**
   * Conté les rutes calculades
   */
  private _routes = new BehaviorSubject<google.maps.DirectionsRoute[]>([]);

  /**
   * @ignore
   */
  private routesData = [];

  /**
   * @ignore
   */
  readonly routes = this._routes.asObservable();

  /**
   * Línies dibuixades al mapa que representen rutes
   */
  private polylines: google.maps.Polyline[] = [];

  /**
   * Rutes renderitzades 
   */
  private previewRoutes: google.maps.DirectionsRenderer[] = [];

  /**
   * Marcadors per indicar les parades de la ruta
   */
  private markers: google.maps.Marker[] = [];

  /**
   * @ignore
   */
  imgUrl: string = "assets/img/";

  /**
   * @ignore
   */
  iconNames: string[] = ["routeIconA.svg","routeIconB.svg","routeIconC.svg","routeIconD.svg","routeIconE.svg","routeIconF.svg","routeIconG.svg","routeIconH.svg"]
  
  /**
   * Preu per kilòmetre de la ruta
   */
  preuKm: number = 0;
  
  /**
   * @ignore
   */
  private _inrixFlag = new BehaviorSubject<boolean>(false);
  
  /**
   * Indica si el càlcul de la ruta s'esta fent a través del servei Inrix
   */
  inrixFlag: boolean = false;

  /**
   * Dades relacionades amb la ruta calculada per Inrix
   */
  inrixData: InrixData;

  /**
   * @ignore
   */
  private _inrixData = new BehaviorSubject<InrixData>(this.inrixData);

  /**
   * Constructor del component
   * @param inrixService Servei per fer crides a Inrix
   * @param http Client http
   */
  constructor(private inrixService: InrixService, private http: HttpClient) { }

  /**
   * Retorna les rutes calculades pel Google Maps Directions
   */
  getRoutes() {
    this._routes.next(this.routesData);
    return this._routes.asObservable();
  }

  /**
   * Retorna les dades de la ruta calculada per Inrix
   */
  getInrix() {
    this._inrixData.next(this.inrixData);
    return this._inrixData.asObservable();
  }

  /**
   * Retorna InrixFlag
   */
  getInrixFlag() {
    this._inrixFlag.next(this.inrixFlag);
    return this._inrixFlag.asObservable();
  }

  /**
   * Retorna si s'ha trobat o no una ruta
   */
  getRouteNotFoundFlag(){
    return this._routeNotFound.asObservable();
  }

  /**
   * Donada una array de direccions representades en strings, calcula la ruta 
   * @param {string[]} waypoints array de strings amb la direcció dels diferents punts de la ruta
   * @param {Boolean} avoidTolls indica si es volen evitar els peatges o no
   */
  getShortestRoute(waypoints: string[], avoidTolls: Boolean){
    this.deleteDirections();
    this.inrixFlag = false;
    this._inrixFlag.next(this.inrixFlag);
    var start = waypoints[0];
    var end = waypoints[waypoints.length-1];
    var waypointsLocations = this.getWaypoints(waypoints.slice(1,waypoints.length-1))
    var routeAlternatives: boolean = waypointsLocations.length === 0; 
    var request: google.maps.DirectionsRequest = {
      origin: start,
      destination: end,
      travelMode: google.maps.TravelMode['DRIVING'],
      provideRouteAlternatives: routeAlternatives,
      waypoints: waypointsLocations,
    };
    if(avoidTolls) request.avoidTolls = true;
    this.directionsService.route(request, (result,status) => {
      if( status === 'OK') {
        this._routeNotFound.next(false);
        this.setMap(this.map);
        this.deleteMarkers();
        this.directionsRenderer.setDirections(result);
        this.addMarkers();
        this.directionsPreviewRenderer.setDirections(result);
        this.directionsPreviewRenderer.setRouteIndex(-1);
        if(result.routes.length > 0){
          this.routesData = result.routes;
          this._routes.next(this.routesData);
          this.drawRoutesPreview(result);
        }
      }
      else{
        this._routeNotFound.next(true);
        this.deleteDirections();
        this.routesData = [];
      }
    })

    return this.routesData;
  }

  /**
   * Donada location, retorna la latitud i longitud segons el servei de localització de Google Maps
   * @param {string} location Nom o descripció d'una localització
   */
  findLatLng(location: string){
    var latlng = "";
    var lat, lng;
    var request: google.maps.places.FindPlaceFromQueryRequest = {
      query: location,
      fields: ['name','geometry']
    }
    this.placesService.findPlaceFromQuery(request, (result, status) => {
      if(status === google.maps.places.PlacesServiceStatus.OK) {
        lat = result[0].geometry.location.lat().toString();
        lng = result[0].geometry.location.lng().toString();
        return lat + ',' + lng;
      }
    });

    
  }

  /**
   * Donada una array de direccions representades en strings, calcula la ruta més ràpida segons el servei Inrix 
   * @param {string[]} waypoints array de strings amb la direcció dels diferents punts de la ruta
   */
  getInrixShortestRoute(waypoints: string[]){
    this.deleteDirections();
    this.inrixFlag = true;
    this._inrixFlag.next(this.inrixFlag);
    var request: google.maps.places.FindPlaceFromQueryRequest;

    var waypointsLatLng: string[] = [];
    var waypointsLocations: google.maps.LatLng[] = [];
    waypoints.forEach((waypointString,index) => {
      this.placesService.findPlaceFromQuery({query: waypointString, fields:['name','geometry']}, (result, status) => {

        if(status === google.maps.places.PlacesServiceStatus.OK) {
          var lat = result[0].geometry.location.lat().toString();
          var lng = result[0].geometry.location.lng().toString();
          var location = new google.maps.LatLng(result[0].geometry.location.lat(),result[0].geometry.location.lng());
          waypointsLocations.push(location);
          var latLngString =  lat + ',' + lng;
          waypointsLatLng.push(latLngString);
          
          if(index === waypoints.length-1 && waypointsLatLng.length == waypoints.length){

            this.http.get(this.inrixService.getFastestPathUrl(waypointsLatLng)).toPromise().then( (data:any)  => {
              var duration = this.secondsToHours(data.result.trip.routes[0].travelTimeMinutes * 60)
              var distance = data.result.trip.routes[0].totalDistance + " km";
              var fuel = (parseInt(data.result.trip.routes[0].totalDistance) * this.preuKm).toFixed(3).toString() + " €";
              var summary = data.result.trip.routes[0].summary.text;
              this.inrixData = {
                distance: distance,
                duration: duration,
                fuel: fuel,
                summary: summary,
                routePoints: this.getInrixPath(data.result.trip.routes[0].points.coordinates),
                bounds: this.getInrixBounds(data.result.trip.routes[0].boundingBox)
              }
              this.drawInrixRoute();
              this.addInrixMarkers(waypointsLocations);
              this.map.fitBounds(this.getInrixBounds(data.result.trip.routes[0].boundingBox));
              this._inrixData.next(this.inrixData);
        
            });
          }
        }
      });

      
    });

    return [waypointsLatLng];
  }

  /**
   * Dibuixa al mapa la ruta calculada per Inrix
   */
  drawInrixRoute(): void{
    this.deletePreviews();
    this.deleteRenderer();
    this.addInrixMarkers([this.inrixData.routePoints[0],this.inrixData.routePoints[this.inrixData.routePoints.length-1]])
    var polilyne = new google.maps.Polyline({
      strokeColor: "#FFFFFF",
      strokeOpacity: 1,
      strokeWeight: 9,
      zIndex: -1,
      map: this.map,
      path: this.inrixData.routePoints
    });
    var polilyne2 = new google.maps.Polyline({
      strokeColor: "#000CFF",
      strokeOpacity: 1,
      strokeWeight: 5,
      zIndex: 1,
      map: this.map,
      path: this.inrixData.routePoints
    });

    this.polylines.push(polilyne,polilyne2);
  }

  /**
   * Donat un boundingBox en format inrix, retorna els mateixos bounds en format google.maps.LatLngBounds 
   * @param boundingBox conté dos punts que representen uns bounds en format nw,se
   * @returns {google.maps.LatLngBounds} Els límits en que es veu tota la ruta format google.maps.LatLngBounds
   */
  getInrixBounds(boundingBox): google.maps.LatLngBounds{
    var southWest = new google.maps.LatLng(boundingBox.corner2.coordinates[0][1], boundingBox.corner1.coordinates[0][0])
    var northEast = new google.maps.LatLng(boundingBox.corner1.coordinates[0][1], boundingBox.corner2.coordinates[0][0])
    return new google.maps.LatLngBounds(southWest,northEast);
  }

  /**
   * Donada una llista de coordenades en format longitud, latitud, retorna una llista de google.maps.LatLng amb les mateixes posiciones
   * @param lngLatPoints Llista de coordenades en format longitud, latitud
   * @returns {google.maps.LatLng[]} Llista de google.maps.LatLng amb tots els punts geomètrics per on passa la ruta
   */
  getInrixPath(lngLatPoints): google.maps.LatLng[]{
    var latLngPoints: google.maps.LatLng[] = [];
    lngLatPoints.forEach(point => {
      var lat = point[1];
      var lng = point[0];
      var latlng = new google.maps.LatLng(lat,lng);
      latLngPoints.push(latlng);
    });

    return latLngPoints;
  }

  /**
   * Donada una llista de coordenades amb els waypoints de la ruta Inrix, pinta al marcadors a aquestes coordenades
   * @param waypoints els waypoints de la ruta Inrix
   */
  addInrixMarkers(waypoints): void {
    var markerIndex = 0;
    var map = this.getMap();
    waypoints.forEach( waypoint => {
      var marker = new google.maps.Marker({
        position: waypoint,
        icon: {
          url: this.imgUrl + this.iconNames[markerIndex],
          scaledSize: new google.maps.Size(40,50)
        },
        map: map
      });
      this.markers.push(marker);
      markerIndex++;

    })

  }

  /**
   * Donada una array que conté strings d'adreces, retorna una array de DirectionsWaypoint que conté aquestes adreces
   * @param {string[]} waypointsStrings Adreces per on passa una ruta
   * @returns {google.maps.DirectionsWaypoint[]} Punts per on passa la ruta en format google.maps.DirectionsWaypoint
   */
  getWaypoints(waypointsStrings: string[]): google.maps.DirectionsWaypoint[]{
    var waypoints: google.maps.DirectionsWaypoint[] = [];
    waypointsStrings.forEach(waypointString => {
      let waypoint: google.maps.DirectionsWaypoint = {
        location: waypointString,
        stopover: true
      };

      waypoints.push(waypoint);
    })


    return waypoints;
  }

  /**
   * Selecciona quina ruta s'activa en cada moment
   * @param {number} index indica índex de la ruta seleccionada dins l'array de rutes
   */
  setActiveRoute(index: number): void{
    this.deleteMarkers();
    this.directionsRenderer.setRouteIndex(index);
    this.addMarkers();
  }

  /**
   * Retorna la ruta seleccionada per l'usuari
   * @returns {google.maps.DirectionsWaypoint} ruta seleccionada per l'usuari
   */
  getActiveRoute(): google.maps.DirectionsRoute{
    return this.routesData[this.directionsRenderer.getRouteIndex()];
  }

  /**
   * Selecciona quina ruta es posa en mode preview
   * @param {number} index indica índex de la ruta seleccionada dins l'array de rutes
   */
  setPreviewRoute(index: number): void{
    this.directionsPreviewRenderer.setRouteIndex(index);
  }

  /**
   * Actualitza el valor del preu per kilòmetre de la ruta
   * @param preuKm nou preu per kilòmetre
   */
  setPreuKm(preuKm: number): void {
    this.preuKm = preuKm;
  }

  /**
   * Indica al servei el mapa on s'han de mostrar els resultats
   * @param {google.maps.Map} map mapa al qual volem mostrar les rutes calculades
   */
  setMap(map: google.maps.Map): void{
    this.map = map;
    this.placesService = new google.maps.places.PlacesService(this.map);
    this.directionsRenderer.setMap(map);
    this.directionsPreviewRenderer.setMap(map);
    if(this.directionsRenderer.getDirections()) this.addMarkers();
  }

  /**
   * Retorna el mapa al qual es mostren les rutes
   * @returns {google.maps.Map} Mapa al qual es mostren les rutes
   */
  getMap(): google.maps.Map{
    return this.map;
  }

  /**
   * Indica on s'ha de mostrar la informació sobre la o les rutes calculades
   * @param div element del document al qual volem posar la informació
   */
  setPanel(div: any): void{
    this.directionsRenderer.setPanel(div);
  }

  /**
   * Pinta al mapa les diferents rutes calculades amb diferents colors
   * @param {google.maps.DirectionsResult} result rutes calculades
   */
  drawRoutesPreview(result: google.maps.DirectionsResult): void{
    this.deletePreviews();
    result.routes.forEach( (route,routeIndex) => {
      var renderer = new google.maps.DirectionsRenderer({
        polylineOptions: {
          strokeColor: "#A7A7A7",
          strokeOpacity: 1,
          strokeWeight: 5,
          zIndex: -1
        },
        suppressMarkers: true
      });
      renderer.setMap(this.map);
      renderer.setRouteIndex(routeIndex);
      renderer.setDirections(result);
      var polilyne = new google.maps.Polyline({
        strokeColor: "#FFFFFF",
        strokeOpacity: 1,
        strokeWeight: 9,
        zIndex: -1,
        map: this.map,
        path: this.calculatePath(renderer.getDirections().routes[routeIndex])
      });
      this.polylines.push(polilyne);
      this.previewRoutes.push(renderer);
    })
  }


  /**
   * Esborra les polylines que hem afegit al mapa
   */
  deletePreviews(): void{
    this.polylines.forEach(polilyne => polilyne.setMap(null) );
    this.previewRoutes.forEach(renderer => renderer.setMap(null));
    this.polylines = [];
    this.previewRoutes = [];
  }

  /**
   * Esborra el renderer del mapa
   */
  deleteRenderer(): void {
    this.directionsRenderer.setMap(null);    
  }

  /**
   * Esborra tota la informació del mapa relacionada amb rutes
   */
  deleteDirections(): void{
    this.deleteMarkers();
    this.deletePreviews();
    this.directionsRenderer.setMap(null);
    this.directionsPreviewRenderer.setMap(null);
    this._routes.next([]);
    this.inrixData = null;
    this._inrixData.next(this.inrixData);

  }

  
  /**
   * Afegeix els marcadors als waypoints de la ruta seleccionada
   */
  addMarkers(): void{
    var index = this.directionsRenderer.getRouteIndex();
    var markerIndex = 0;
    var legs: google.maps.DirectionsLeg[] = this.directionsRenderer.getDirections().routes[index].legs;
    var map = this.directionsRenderer.getMap();
    legs.forEach( (leg,index) => {
      var markerStart = new google.maps.Marker({
        position: leg.start_location,
        icon: {
          url: this.imgUrl + this.iconNames[markerIndex],
          scaledSize: new google.maps.Size(40,50)
        },
        map: map
      });
      this.markers.push(markerStart);
      markerIndex++;

      if(index === legs.length-1){
        var markerEnd = new google.maps.Marker({
          position: leg.end_location,
          icon: {
            url: this.imgUrl + this.iconNames[markerIndex],
            scaledSize: new google.maps.Size(40,50)
          },
          map: map
        });
        this.markers.push(markerEnd);
        markerIndex++;
      }
    })
  }

  /**
   * Esborra els marcadors de la ruta seleccionada
   */
  deleteMarkers(): void{
    this.markers.forEach(marker => marker.setMap(null));
    this.markers = [];
  }

  /**
   * Donada una ruta, retorna una array que conté el path d'aquesta ruta
   * @param {google.maps.DirectionsRoute} route ruta per la qual volem calcular el path
   * @returns {google.maps.LatLng[]} Path de la ruta en format google.maps.LatLng
   */
  calculatePath(route: google.maps.DirectionsRoute): google.maps.LatLng[]{
    var path = [];
    route.legs.forEach(leg => {
      leg.steps.forEach(step => {
        step.path.forEach( latlng => {
          path.push(latlng);
        })
      })
    })

    return path;
  }

  /**
   * Donada una ruta, calcula la seva distància total i retorna aquesta distància en format de text
   * @param {google.maps.DirectionsRoute} route ruta per la qual volem calcular la distància
   * @returns Distància en format text
   */
  calcDistance(route:google.maps.DirectionsRoute): string{
    var distance = 0;
    route.legs.forEach( leg => {
      distance += leg.distance.value;
    })

    if(distance < 1000) return distance + " m";
    else return (distance/1000).toFixed(2) + " km";
  }

  /**
   * Donada una ruta, retorna un string amb el temps que es triga en completar-la
   * @param {google.maps.DirectionsRoute} route ruta per la qual volem calcular la duració
   * @returns Duració en format text
   */
  calcDuration(route: google.maps.DirectionsRoute): string{
    var duration = 0;
    route.legs.forEach( leg => {
      duration += leg.duration.value;
    })

    return this.secondsToHours(duration);
  }

  /**
   * Donada una ruta i un preu per km, retorna el preu total del combustible per la ruta
   * @param {google.maps.DirectionsRoute} route ruta per la qual volem calcular el consum de combustible
   * @param {number} preuKm Cost en € de combustible per cada km
   */
  calcFuel(route: google.maps.DirectionsRoute): number{
    var distance = 0;
    route.legs.forEach( leg => {
      distance += leg.distance.value;
    })

    return ((distance/1000)*this.preuKm);
  }

  /**
   * Donat uns segons, retorna una string que representa aquest temps en hores i/o minuts
   * @param {number} seconds segons a convertir en una string d'hores i minuts
   * @returns {string} retorna el temp en hores i/o minuts
   */
  secondsToHours(seconds: number): string{
    var hrsString = Math.floor(seconds/3600).toString();
    var min = Math.floor((seconds%3600)/60);
    var minString = Math.floor((seconds%3600)/60).toString();
    var secondsString = (seconds % 60).toString();
    if(seconds<10) secondsString = "0" + secondsString;
    if(min) minString = "0" + minString;
    if (hrsString === "0") return min + "min";
    else return hrsString + "h " + min + "min";
  }

}
